/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (edit
 * mode — the page-with-back-button + FormGroup field layout), same as `catalogue/brands/Detail.tsx`.
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState } from "react";
import { Link, useLoaderData, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import {
  PiCheckCircleDuotone as CheckCircle,
  PiClockDuotone as ClockIcon,
  PiIdentificationBadgeDuotone as IdentityIcon,
  PiTagDuotone as Tag,
  PiListChecksDuotone as RulesIcon,
  PiShoppingCartDuotone as PurchaseIcon,
  PiToggleLeftDuotone as StatusIcon,
  PiBuildingsDuotone as CompanyIcon,
} from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import { PageHeader, ErrorSurface, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import { DateField } from "../../components/date-field";
import { byKey, getTier, getTierBySlug, updateTier } from "../../lib/services/catalog";
import { tierPath } from "../../lib/paths";
import { can } from "../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../lib/form-layout";
import { SectionTitle } from "../../components/section-title";
import { RecordSummary } from "../../components/detail-parts";
import type { AccountSummary, PermissionState } from "../../types";
import { Alert, Badge, Button } from "rizzui";
import { CheckboxCard } from "../../components/checkbox-card";
import { StatusSwitch } from "../../components/status-switch";
import { formatDate, parseIsoDate, toIsoDate } from "../../lib/format";
import { useGoBack } from "../../lib/navigation";
import { FOR_NEW_COMPANIES, PIECE_PURCHASE, ROUNDING_HINT, ROUNDING_LABEL, ROUNDING_MODES, UNIT_PURCHASE } from "../../lib/tier-text";
import { PricingFlow } from "../../components/pricing-flow";

/**
 * T061 — editable tier settings, `PUT /tiers/{id}`. Read side is `GET /tiers/{tierId}`
 * (`PricingController.GetTierSummary` → `TierSummaryDto`) — `TiersController.cs`'s own comment
 * says this is what serves `GET /tiers/{id}` — there is no separate list-shaped single-tier read.
 * Reached from the tier list's name cell (T059); Cancel returns there.
 */
export async function tierDetailLoader({ params, request }: LoaderFunctionArgs) {
  const tier = await byKey(request, params.slug!, getTier, getTierBySlug, tierPath);
  if (!tier) throw new Error("The API returned no body for this tier.");
  return { tier };
}


const tierEditSchema = z.object({
  name: z.string().trim().min(1, "Required"),
  effectiveFrom: z.string().trim().min(1, "Required"),
  priceRoundingMode: z.enum(["none", "rupee", "nearest_5"]),
  allowPiecePurchase: z.boolean(),
  allowUnitPurchase: z.boolean(),
  isActive: z.boolean(),
  forNewCompanies: z.boolean(),
});
type TierEditValues = z.infer<typeof tierEditSchema>;

export default function TierDetail() {
  const { tier } = useLoaderData() as Awaited<ReturnType<typeof tierDetailLoader>>;
  const goBack = useGoBack("/tiers");
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [error, setError] = useState<unknown>(null);

  if (!can(permissionState, "pricing.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this tier" />;
  }
  const canWrite = can(permissionState, "pricing.administer");

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title={tier.name}
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Pricing" }, { name: "Tiers", href: "/tiers" }, { name: tier.name }]}
      />
      {/* Status and the tier's own sub-screens in the page, never the top bar. */}
      <RecordSummary
        meta={
          <>
            {tier.isDefault ? (
              <Badge variant="flat" color="primary" rounded="md">
                Default Tier
              </Badge>
            ) : null}
            {tier.forNewCompanies ? (
              <Badge variant="flat" color="secondary" rounded="md">
                New companies
              </Badge>
            ) : null}
          </>
        }
        links={
          <>
            {/* T065-T070's own screen — additive wiring from this bucket (partitioning note: the
                route table's T059 comment left "this list's own screen to wire" for whoever built
                the target; this is that one small link, not a rewrite of this file). */}
            <Link to={`${tierPath(tier.slug)}/prices`}>
              <Button as="span" variant="outline" size="sm">
                <Tag aria-hidden className="me-1.5 size-4" />
                Prices
              </Button>
            </Link>
            <Link to={`${tierPath(tier.slug)}/rules`}>
              <Button as="span" variant="outline" size="sm">
                <RulesIcon aria-hidden className="me-1.5 size-4" />
                Discount rules
              </Button>
            </Link>
          </>
        }
      />
      {tier.isDefault ? (
        <Alert color="info" variant="flat" icon={<CheckCircle aria-hidden className="size-4" />}>
          This is the <strong>Default Tier</strong>. Its prices are the base prices set on each SKU (SKU → Base prices) — it
          has no discount rules of its own and is never generated. Guests and customers with no other tier pay these prices.
        </Alert>
      ) : (
        <PricingFlow tierSlug={tier.slug} />
      )}

      {/* isEffective is server-resolved (FR-019a) from effectiveFrom vs. today — never
          recomputed here (constitution I). Colour follows the fact, not decoration. */}
      <Alert
        color={tier.isEffective ? "success" : "warning"}
        variant="flat"
        icon={tier.isEffective ? <CheckCircle aria-hidden className="size-4" /> : <ClockIcon aria-hidden className="size-4" />}
        className=""
      >
        {tier.isEffective
          ? "In effect — its prices are what customers assigned to it see today."
          : `Not in effect yet — you can set it up now, but customers are not offered its prices until ${formatDate(tier.effectiveFrom)}.`}
      </Alert>

      {error ? <ErrorSurface error={error} action="save this tier" /> : null}

      <Form<TierEditValues>
        validationSchema={tierEditSchema}
        useFormProps={{
          defaultValues: {
            name: tier.name,
            effectiveFrom: tier.effectiveFrom,
            priceRoundingMode: tier.priceRoundingMode,
            allowPiecePurchase: tier.allowPiecePurchase,
            allowUnitPurchase: tier.allowUnitPurchase,
            isActive: tier.isActive,
            forNewCompanies: tier.forNewCompanies,
          },
        }}
        className={formCard}
        onSubmit={async (values) => {
          setError(null);
          try {
            await updateTier(tier.id, {
              name: values.name,
              allowPiecePurchase: values.allowPiecePurchase,
              allowUnitPurchase: values.allowUnitPurchase,
              priceRoundingMode: values.priceRoundingMode,
              effectiveFrom: values.effectiveFrom,
              isActive: values.isActive,
              // Sent only when it changed, so saving another field never moves the mark.
              forNewCompanies: values.forNewCompanies === tier.forNewCompanies ? undefined : values.forNewCompanies,
            });
            toast.success("Tier saved");
            goBack();
          } catch (err) {
            setError(err);
            toast.error("Could not save the tier");
          }
        }}
      >
        {({ register, watch, setValue, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              <FormGroup
                title={<SectionTitle icon={IdentityIcon}>Tier details</SectionTitle>}
                description="What it's called, when it starts applying, and how its prices round."
              >
                    <Field label="Name" required disabled={!canWrite} error={errors.name?.message} {...register("name")} />
                    <DateField
                      required
                      dateFormat="dd-MMM-yyyy"
                      disabled={!canWrite}
                      placeholderText="Choose a date…"
                      selected={parseIsoDate(watch("effectiveFrom") || null)}
                      onChange={(date) => setValue("effectiveFrom", date ? toIsoDate(date as Date) : "", { shouldValidate: true })}
                      inputProps={{
                        label: "Effective from",
                        disabled: !canWrite,
                        helperText: "Until this date the tier is administrable but nothing it holds is offered for sale.",
                        error: errors.effectiveFrom?.message,
                        clearable: canWrite && Boolean(watch("effectiveFrom")),
                        onClear: () => setValue("effectiveFrom", "", { shouldValidate: true }),
                      }}
                    />
                  <SelectField
                    label="Rounding mode"
                    required
                    hint={ROUNDING_HINT}
                    disabled={!canWrite}
                    error={errors.priceRoundingMode?.message}
                    {...register("priceRoundingMode")}
                  >
                    {ROUNDING_MODES.map((mode) => (
                      <option key={mode} value={mode}>
                        {ROUNDING_LABEL[mode]}
                      </option>
                    ))}
                  </SelectField>
              </FormGroup>

              <FormGroup
                title={<SectionTitle icon={PurchaseIcon}>Purchase types</SectionTitle>}
                description="Which units customers on this tier may buy in."
              >
                <fieldset className="col-span-full grid grid-cols-1 gap-4 @xl:grid-cols-2" disabled={!canWrite}>
                  <legend className="sr-only">Purchase types</legend>
                  <CheckboxCard {...PIECE_PURCHASE} {...register("allowPiecePurchase")} />
                  <CheckboxCard {...UNIT_PURCHASE} {...register("allowUnitPurchase")} />
                </fieldset>
              </FormGroup>

              {/* The Default Tier is B2C, so it can never be the tier new companies start on (422). */}
              {tier.isDefault ? null : (
                <FormGroup
                  title={<SectionTitle icon={CompanyIcon}>New companies</SectionTitle>}
                  description="Which tier a company is placed on when it registers."
                >
                  <fieldset className="col-span-full grid grid-cols-1 gap-4 @xl:grid-cols-2" disabled={!canWrite}>
                    <legend className="sr-only">New companies</legend>
                    <CheckboxCard {...FOR_NEW_COMPANIES} {...register("forNewCompanies")} />
                  </fieldset>
                </FormGroup>
              )}

              <FormGroup title={<SectionTitle icon={StatusIcon}>Status</SectionTitle>} description="Whether the tier can be given to customers.">
                <StatusSwitch
                  className="@xl:col-span-2"
                  disabled={!canWrite}
                  activeText={tier.isDefault ? "The Default Tier cannot be deactivated." : "Offered for new assignment."}
                  inactiveText="Hidden from new assignment. Nothing is deleted."
                  {...register("isActive")}
                />
              </FormGroup>
            </div>

            {canWrite ? (
              <FormFooter
                className={formCardFooter}
                submitBtnText={isSubmitting ? "Saving…" : "Save changes"}
                isLoading={isSubmitting}
                altBtnText="Cancel"
                handleAltBtn={goBack}
              />
            ) : null}
          </>
        )}
      </Form>
    </div>
  );
}
